import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ClipboardList, PackageSearch, Wrench } from "lucide-react";

import { mock } from "@/mock";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

function Glass({ className, children }) {
  return (
    <div
      className={cn(
        "glass rounded-3xl shadow-[0_18px_50px_rgba(0,0,0,0.4)]",
        "transition-shadow duration-200 hover:shadow-[0_0_26px_rgba(0,194,255,0.3)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function ServiceDetail({ service }) {
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="text-xs tracking-[0.22em] text-white/55">
            SERVICE
          </div>
          <div className="mt-2 text-2xl font-semibold tracking-tight text-white">
            {service.title}
          </div>
          <p className="mt-3 text-sm leading-relaxed text-white/70">
            {service.short}
          </p>
        </div>
        <div className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-black/55">
          <Wrench className="h-5 w-5 text-primary" />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
          <div className="flex items-center gap-2 text-sm font-medium text-white">
            <PackageSearch className="h-4 w-4 text-primary" />
            Problems we solve
          </div>
          <div className="mt-3 space-y-2">
            {service.problems.map((p) => (
              <div
                key={p}
                className="rounded-xl border border-white/10 bg-black/35 px-4 py-3 text-sm text-white/75"
              >
                {p}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
          <div className="flex items-center gap-2 text-sm font-medium text-white">
            <ClipboardList className="h-4 w-4 text-primary" />
            Process
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {service.process.map((p, idx) => (
              <div
                key={p}
                className="rounded-xl border border-white/10 bg-black/35 px-4 py-3"
              >
                <div className="text-xs text-white/55">0{idx + 1}</div>
                <div className="mt-1 text-sm text-white/80">{p}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
          <div className="text-sm font-medium text-white">Deliverables</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {service.deliverables.map((d) => (
              <Badge
                key={d}
                variant="secondary"
                className="rounded-full border-white/10 bg-white/[0.05] text-white/80 hover:bg-white/[0.05]"
              >
                {d}
              </Badge>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
          <div className="text-sm font-medium text-white">Tools & technologies</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {service.tools.map((t) => (
              <Badge
                key={t}
                variant="secondary"
                className="rounded-full border-white/10 bg-white/[0.05] text-white/80 hover:bg-white/[0.05]"
              >
                {t}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Services() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const services = useMemo(() => mock.services, []);

  const defaultId = services[0]?.id;
  const active = (hash || "").replace("#", "") || defaultId;
  const activeService = services.find((s) => s.id === active) || services[0];

  return (
    <div className="w-full px-5 py-16 xl:px-10 2xl:px-14">
      <>
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-black/40 px-4 py-2 text-xs text-white/70">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          Services
        </div>
      </>

      <>
        <h1 className="mt-5 text-3xl font-semibold tracking-tight text-white md:text-4xl">
          Services that cover the full product lifecycle
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/70">
          We help you go from idea to deployment—with a premium UI, clean code,
          and predictable execution.
        </p>
      </>

      <Separator className="my-12 bg-white/10" />

      {/* Desktop: Tabs */}
      <>
        <div className="hidden lg:block">
          <Tabs value={active} onValueChange={(v) => navigate(`/services#${v}`)}>
            <TabsList className="w-full justify-start gap-1 rounded-2xl border border-primary/20 bg-black/40 p-1 shadow-[0_14px_40px_rgba(0,0,0,0.45)]">
              {services.map((s) => (
                <TabsTrigger
                  key={s.id}
                  value={s.id}
                  className={cn(
                    "rounded-xl px-4 py-2 text-sm",
                    "data-[state=active]:bg-primary/15 data-[state=active]:border data-[state=active]:border-primary/35 data-[state=active]:text-white",
                    "text-white/70 hover:text-white",
                  )}
                >
                  {s.title}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value={active} className="mt-6">
              <Glass className="p-8">
                <ServiceDetail service={activeService} />
                <div className="mt-8">
                  <Button
                    //onClick={() => navigate("/contact#form")}
                    onClick={() => window.open("https://cal.com/vadri-2cqnbp", "_blank")}
                    className="h-[52px] rounded-xl bg-primary px-6 text-black btn-glow hover:bg-primary/90"
                  >
                    Get a Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </Glass>
            </TabsContent>
          </Tabs>
        </div>

        {/* Mobile/Tablet: Accordion */}
        <div className="lg:hidden">
          <Accordion type="single" collapsible className="w-full">
            {services.map((s) => (
              <AccordionItem key={s.id} value={s.id} className="border-white/10">
                <AccordionTrigger className="text-white/90 hover:no-underline">
                  {s.title}
                </AccordionTrigger>
                <AccordionContent className="text-white/70">
                  <Glass className="p-5">
                    <ServiceDetail service={s} />
                    <Button
                      //onClick={() => navigate("/contact#form")}
                      onClick={() => window.open("https://cal.com/vadri-2cqnbp", "_blank")}
                      className="mt-6 h-[48px] w-full rounded-xl bg-primary text-black btn-glow hover:bg-primary/90"
                    >
                      Get a Quote
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Glass>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </>

     {/* <Separator className="my-12 bg-white/10" />

      <>
        <div className="glass rounded-3xl p-8 shadow-[0_18px_50px_rgba(0,0,0,0.4)]">
          <div className="text-sm font-medium text-white">Need a custom scope?</div>
          <div className="mt-2 text-sm text-white/70">
            Tell us your constraints (budget, timeline, integrations) and we’ll tailor an engagement model.
          </div>
          <div className="mt-6">
            <Button
              onClick={() => navigate("/pricing")}
              variant="secondary"
              className="h-[52px] rounded-xl border border-primary/25 bg-black/50 px-6 text-white hover:bg-primary/10"
            >
              View pricing & models
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </>*/}
    </div>
  );
}
